// T22.359: colour choice for a Segment APPENDED to the wheel ("+ Segment",
// Segment Library add, capture auto-create -- every caller passes
// index === wheelData.length).
//
// getColor used to take the palette colour at `index % palette.length`, i.e. by
// SEGMENT COUNT. After a delete the count drops but the surviving Segments keep
// their colours, so the count-indexed colour is usually one still on the wheel
// (MEASURED web-stg main.c0df4d75.js, wheel 508787, 5-colour palette: delete
// Segment 3 (#34D399), + Segment -> #F472B6, the colour Segment 5 already had,
// while #34D399 sat unused). The no-palette branch only excluded the first and
// last Segment's colours, so any middle Segment's colour could repeat too.
//
// Pure functions so the rule is unit-assertable without the hook.

const norm = (c) => (typeof c === "string" ? c.trim().toUpperCase() : null);

const colorCounts = (wheelData) => {
  const counts = new Map();
  (wheelData || []).forEach((seg) => {
    const c = norm(seg?.settings?.color);
    if (c) counts.set(c, (counts.get(c) || 0) + 1);
  });
  return counts;
};

// Palette wheels: walk the palette from the count-indexed slot (so a wheel that
// never had a delete gets exactly the colour it always got) and return the first
// colour no Segment uses. Once every palette colour is taken, return the
// least-used one, preferring one that differs from the ring neighbours the new
// Segment lands between (the last and the first Segment).
export const pickPaletteColorForNewSegment = (wheelData, index, paletteHexes) => {
  const hexes = (paletteHexes || []).filter(Boolean);
  if (!hexes.length) return undefined;
  const start = index % hexes.length;
  const order = hexes.map((_, i) => hexes[(start + i) % hexes.length]);
  const counts = colorCounts(wheelData);

  const unused = order.find((hex) => !counts.has(norm(hex)));
  if (unused) return unused;

  const data = wheelData || [];
  const neighbours = new Set(
    [data[data.length - 1], data[0]].map((s) => norm(s?.settings?.color))
  );
  const score = (hex) =>
    (counts.get(norm(hex)) || 0) * 2 + (neighbours.has(norm(hex)) ? 1 : 0);
  return order.reduce((best, hex) => (score(hex) < score(best) ? hex : best));
};

// No-palette wheels: random pick from `candidates` that no Segment uses yet;
// when the wheel already uses every candidate, fall back to the old rule (not
// the first or last Segment's colour).
export const pickRandomColorForNewSegment = (
  wheelData,
  candidates,
  random = Math.random
) => {
  const data = wheelData || [];
  const counts = colorCounts(data);
  let pool = candidates.filter((c) => !counts.has(norm(c)));
  if (!pool.length) {
    const edge = [data[data.length - 1], data[0]].map((s) => norm(s?.settings?.color));
    pool = candidates.filter((c) => !edge.includes(norm(c)));
  }
  if (!pool.length) pool = candidates;
  return pool[Math.floor(random() * pool.length)];
};
